import { lazy } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { pageNames } from '@common/routes/pageNames';
import ClosedAgreement from '@dues/pages/Partner/ClosedAgreement';
import { CheckoutGroup, routes as renegKitRoutes, useRKOffersListingExperiment } from '@libs/renegkit';
import { useUserStore } from '@libs/acp/store/user/useUserStore';
import { identityUpdateContactSendCode, identityUpdateContactVerifyCode } from '@libs/acp/apis/updateAccount/updateAccountAPI';
import { maskCellphone } from '@libs/utils/masks/cellphone';
import { useCheckoutSessionConfig } from '@dues/hooks/useCheckoutSessionConfig';
import useGTM from '@common/hooks/useGTM';

const MyDebts = lazy(() => import('@dues/pages/MyDebts'));
const Debts = lazy(() => import('@dues/pages/Debts'));

const Confirmation = lazy(() => import('@dues/pages/Partner/Confirmation'));
const ClosedAgreementError = lazy(() => import('@dues/pages/Partner/ClosedAgreementError'));

const Agreement = lazy(() => import('@dues/pages/RKAgreement'));
const Home = lazy(() => import('@dues/pages/RKHome'));
const Offers = lazy(() => import('@dues/pages/RKOffers'));
const NegativeOffers = lazy(() => import('@dues/pages/RKNegativeOffers'));
const AgreementComposition = lazy(() => import('@dues/pages/RKAgreement/Composition'));
const AgreementConditions = lazy(() => import('@dues/pages/RKAgreement/Conditions'));
const MyAgreements = lazy(() => import('@dues/pages/RKMyAgreements'));

const BilletNotFoundError = lazy(() => import('@dues/components/BilletNotFoundError'));

const NegativeDebtsPage = lazy(() => import('@dues/pages/NegativeDebts'));

export function Router() {
  // A/B dx-rk-offers-listing: variant users get the new home (/inicio / RKHome) at the debts
  // route. This redirect is the SAFETY NET for deep-links / bookmarks / cross-app
  // entries that still hit /minhas-dividas; primary nav links point to /inicio
  // directly (see getHomePath) so the common flow has no redirect hop.
  const { isRKOffersListingVariant } = useRKOffersListingExperiment();

  // #region rk checkout configs and tracking - TEMP: improve
  const { authenticatedUser, addAuthenticatedUser } = useUserStore();
  const customer = authenticatedUser?.acp?.customer;
  const checkoutSessionConfig = useCheckoutSessionConfig();
  const trackGTM = useGTM();

  const checkoutContactConfig = {
    isNCADCheckout: customer?.required_updates?.includes('account_ncad'),
    isNeverEngaged: false, // TODO: check
    userEmail: customer?.email ?? '',
    userPhone: maskCellphone(customer?.phone ?? ''),
    onSendCode: async (payload: { type: 'celular' | 'e-mail'; contact: string }) => {
      const contactType = payload.type === 'celular' ? 'phone' : 'email';
      return identityUpdateContactSendCode({ type: contactType, contact: payload.contact });
    },
    onVerifyCode: async (payload: { type: 'celular' | 'e-mail'; contact: string; token: string }) => {
      const isPhoneFlow = payload.type === 'celular';
      const contactType = isPhoneFlow ? 'phone' : 'email';
      await identityUpdateContactVerifyCode({ type: contactType, token: payload.token });
      addAuthenticatedUser(
        {
          ...authenticatedUser.acp,
          customer: {
            ...customer,
            ...(isPhoneFlow ? { phone: payload.contact } : { email: payload.contact }),
          },
        },
        'acp'
      );
    },
  };

  // #endregion

  return (
    <>
      <Route path={pageNames.confirmation.path} element={<Confirmation />} />
      <Route path={pageNames.agreementClosed.path} element={<ClosedAgreement />} />
      <Route path={pageNames.agreementClosedError.path} element={<ClosedAgreementError />} />

      {...CheckoutGroup({ contactConfig: checkoutContactConfig, sessionConfig: checkoutSessionConfig, trackGTM: trackGTM })}

      {/* #region listing offers a/b test */}
      <Route path={pageNames.myDebts.path} element={isRKOffersListingVariant ? <Navigate to={renegKitRoutes.home.path} replace /> : <MyDebts />} />
      <Route path={renegKitRoutes.home.path} element={<Home />} />

      {/* /dividas */}
      <Route path={pageNames.debts.path} element={isRKOffersListingVariant ? <Offers /> : <Debts />} />
      {/* <Route path={renegKitRoutes.offers.path} element={<Offers />} /> */}

      {/* dividas-negativadas */}
      <Route path={pageNames.negativeDebts.path} element={isRKOffersListingVariant ? <NegativeOffers /> : <NegativeDebtsPage />} />
      {/* <Route path={renegKitRoutes.negativeOffers.path} element={<NegativeOffers />} /> */}
      {/* #endregion */}

      <Route path={renegKitRoutes.myAgreements.path} element={<MyAgreements />} />
      <Route path={renegKitRoutes.agreement.path} element={<Agreement />} />
      <Route path={renegKitRoutes.agreementComposition.path} element={<AgreementComposition />} />
      <Route path={renegKitRoutes.agreementConditions.path} element={<AgreementConditions />} />

      <Route path={pageNames.billetNotFoundError.path} element={<BilletNotFoundError />} />
    </>
  );
}
